import React from 'react';
import { motion } from 'framer-motion';
import { FaCar, FaClock, FaMoneyBillWave, FaGraduationCap, FaUsers, FaWhatsapp, FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import { MdLocalCarWash, MdIron } from 'react-icons/md';
import { Link } from 'react-router-dom';

const CareersPage = () => {
    const jobs = [
        {
            id: 'car-cleaning',
            title: 'Car Cleaning Prez Staff',
            tagline: 'Early Bird Prez Staff: Kickstart Hyderabad\'s Day with Pristine Vehicles',
            icon: MdLocalCarWash,
            timing: '3 AM to 8 AM',
            type: 'Part-time',
            description: 'Join our car cleaning team and be part of Hyderabad\'s premium doorstep car care service. Perfect for night owls seeking short, high-impact shifts.',
            duties: [
                'Thorough exterior and interior cleaning',
                'Attention to detail in every clean',
                'Quick turnaround on each vehicle',
                'Using waterless cleaning technology',
                'Maintaining cleaning equipment',
            ],
            requirements: [
                'Reliability and punctuality',
                'Basic tools knowledge',
                'Physical fitness for outdoor work',
                'Positive attitude and team spirit',
                'No prior experience needed - training provided',
            ],
            perks: [
                'Steady pay with weekly payouts',
                'Transport allowance',
                'Performance bonuses',
                'Flexible scheduling',
            ],
            color: 'from-primary-600 to-primary-800',
        },
        {
            id: 'ironing',
            title: 'Ironing Prez Staff',
            tagline: 'Precision Prez Staff: Transform Ironing into Perfection',
            icon: MdIron,
            timing: 'Flexible Hours',
            type: 'Part-time / Full-time',
            description: 'Be part of our steam ironing team. We\'re looking for energetic team players with a passion for delivering crisp, perfectly pressed garments.',
            duties: [
                'Steam ironing garments crisply',
                'Handling bulk orders efficiently',
                'Maintaining ironing equipment',
                'Quality checking each piece',
                'Organizing and packaging clothes',
            ],
            requirements: [
                'Ironing experience preferred',
                'Eye for detail and quality',
                'Ability to handle bulk work',
                'Time management skills',
                'Team player attitude',
            ],
            perks: [
                'Competitive wages',
                'Weekly payouts',
                'Growth to lead roles',
                'Incentives for quality output',
            ],
            color: 'from-primary-600 to-primary-800',
        },
    ];

    const benefits = [
        {
            icon: FaClock,
            title: 'Flexible Schedules',
            description: 'Part-time schedules fitting local lifestyles',
        },
        {
            icon: FaMoneyBillWave,
            title: 'Competitive Pay',
            description: 'Weekly payouts with performance bonuses',
        },
        {
            icon: FaGraduationCap,
            title: 'Training & Growth',
            description: 'On-the-job training, safe gear, and career paths',
        },
        {
            icon: FaUsers,
            title: 'Referral Bonuses',
            description: 'Earn extra for bringing in trusted peers',
        },
    ];

    return (
        <div className="min-h-screen pt-20">
            {/* Hero Section */}
            <section className="section-padding relative overflow-hidden bg-white">
                <div className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-20 bg-primary-200"></div>
                <div className="max-w-7xl mx-auto text-center relative z-10">
                    <motion.span
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-6 bg-primary-50 text-primary-600"
                    >
                        Join Our Team
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-gray-900"
                    >
                        Build Your Career with <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3CCFDB] to-[#5AE07D] drop-shadow-md pb-2">Goprezz</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-lg md:text-xl max-w-3xl mx-auto mb-8 text-gray-600"
                    >
                        Be part of Hyderabad's fastest-growing doorstep services company.
                        We're looking for dedicated, energetic team players who want to make a difference.
                    </motion.p>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="section-padding relative overflow-hidden bg-gray-50">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-10 bg-primary-200"></div>
                <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full blur-3xl opacity-10 bg-primary-200"></div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
                            Key <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3CCFDB] to-[#5AE07D] drop-shadow-md pb-2">Benefits</span>
                        </h2>
                        <p className="max-w-2xl mx-auto text-gray-600">
                            Why work with Goprezz? Here's what makes us different.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {benefits.map((benefit, index) => (
                            <motion.div
                                key={benefit.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="p-6 rounded-2xl text-center transition-all duration-300 hover:scale-105 bg-white border border-gray-100 shadow-sm hover:shadow-lg"
                            >
                                <div className="w-14 h-14 rounded-2xl bg-primary-50 flex items-center justify-center mx-auto mb-4">
                                    <benefit.icon className="w-7 h-7 text-primary-600" />
                                </div>
                                <h3 className="text-lg font-bold mb-2 text-gray-900">{benefit.title}</h3>
                                <p className="text-sm text-gray-600">{benefit.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Job Listings */}
            <section className="section-padding bg-white">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                            Open <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3CCFDB] to-[#5AE07D] drop-shadow-md pb-2">Positions</span>
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Find the perfect role that fits your skills and schedule.
                        </p>
                    </motion.div>

                    <div className="space-y-8">
                        {jobs.map((job, index) => (
                            <motion.div
                                key={job.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100"
                            >
                                {/* Job Header */}
                                <div className={`bg-gradient-to-r ${job.color} p-6 md:p-8`}>
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <div className="w-16 h-16 rounded-2xl bg-white/30 backdrop-blur-sm flex items-center justify-center">
                                                <job.icon className="w-8 h-8 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="text-2xl font-bold text-white">{job.title}</h3>
                                                <p className="text-sm font-medium" style={{ color: '#ffffff' }}>{job.tagline}</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-3">
                                            <span className="px-4 py-2 rounded-full bg-white/30 text-white text-sm font-medium">
                                                {job.timing}
                                            </span>
                                            <span className="px-4 py-2 rounded-full bg-white/30 text-white text-sm font-medium">
                                                {job.type}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Job Content */}
                                <div className="p-6 md:p-8">
                                    <p className="text-gray-600 mb-6 text-lg">{job.description}</p>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                        {/* Duties */}
                                        <div className="bg-gray-50 rounded-2xl p-5">
                                            <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                                                <span className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center">
                                                    📋
                                                </span>
                                                Key Duties
                                            </h4>
                                            <ul className="space-y-2">
                                                {job.duties.map((duty) => (
                                                    <li key={duty} className="flex items-start gap-2 text-gray-600 text-sm">
                                                        <FaCheckCircle className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
                                                        {duty}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Requirements */}
                                        <div className="bg-gray-50 rounded-2xl p-5">
                                            <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                                                <span className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center">
                                                    ✅
                                                </span>
                                                Requirements
                                            </h4>
                                            <ul className="space-y-2">
                                                {job.requirements.map((req) => (
                                                    <li key={req} className="flex items-start gap-2 text-gray-600 text-sm">
                                                        <FaCheckCircle className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
                                                        {req}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Perks */}
                                        <div className="bg-gray-50 rounded-2xl p-5">
                                            <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                                                <span className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center">
                                                    🎁
                                                </span>
                                                Perks
                                            </h4>
                                            <ul className="space-y-2">
                                                {job.perks.map((perk) => (
                                                    <li key={perk} className="flex items-start gap-2 text-gray-600 text-sm">
                                                        <FaCheckCircle className="w-4 h-4 text-accent-500 mt-0.5 flex-shrink-0" />
                                                        {perk}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>

                                    {/* Apply Button */}
                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <a
                                            href="https://wa.me/919059651065?text=Hi! I'm interested in the position of ${encodeURIComponent(job.title)}"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center justify-center px-8 py-4 rounded-full font-semibold bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:from-primary-600 hover:to-primary-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                                        >
                                            <FaWhatsapp className="w-5 h-5 mr-2" />
                                            Apply via WhatsApp
                                        </a>
                                        <Link
                                            to="/contact"
                                            className="inline-flex items-center justify-center px-8 py-4 rounded-full font-semibold border-2 border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white transition-all duration-300"
                                        >
                                            Contact HR
                                            <FaArrowRight className="w-4 h-4 ml-2" />
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="section-padding bg-gray-50">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="rounded-3xl p-8 md:p-12 shadow-2xl"
                        style={{ background: 'linear-gradient(135deg, #1a8a9c 0%, #0d6b7a 100%)' }}
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                            Ready to Join Goprezz?
                        </h2>
                        <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
                            Don't see a position that fits? We're always looking for talented people.
                            Reach out to us and let's explore opportunities together.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a
                                href="https://wa.me/919059651065?text=Hi! I'm interested in working with Goprezz"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center px-8 py-4 rounded-full font-semibold bg-white text-primary-600 hover:bg-white/90 shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                                <FaWhatsapp className="w-5 h-5 mr-2" />
                                Send Your Resume
                            </a>
                            <Link
                                to="/contact"
                                className="inline-flex items-center justify-center px-8 py-4 rounded-full font-semibold bg-white text-primary-600 hover:bg-white/90 shadow-lg transition-all duration-300"
                            >
                                Contact Us
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default CareersPage;
